/**
 * Date Range Utilities
 * Helper functions for calculating date ranges and timestamps
 */

/**
 * Date range presets
 */
export const DATE_RANGE_PRESETS = {
  TODAY: 'today',
  THIS_WEEK: 'this_week',
  NEXT_7_DAYS: 'next_7_days',
  THIS_MONTH: 'this_month',
  NEXT_30_DAYS: 'next_30_days',
  LAST_HOUR: 'last_hour',
  LAST_7_DAYS: 'last_7_days',
  LAST_30_DAYS: 'last_30_days',
  LAST_90_DAYS: 'last_90_days',
  LAST_180_DAYS: 'last_180_days'
}

// Default date range
export const DEFAULT_DATE_RANGE = DATE_RANGE_PRESETS.NEXT_7_DAYS

/**
 * Date range display labels
 */
export const DATE_RANGE_LABELS = {
  [DATE_RANGE_PRESETS.TODAY]: 'Today',
  [DATE_RANGE_PRESETS.THIS_WEEK]: 'This week',
  [DATE_RANGE_PRESETS.NEXT_7_DAYS]: 'Next 7 days',
  [DATE_RANGE_PRESETS.THIS_MONTH]: 'This month',
  [DATE_RANGE_PRESETS.NEXT_30_DAYS]: 'Next 30 days',
  [DATE_RANGE_PRESETS.LAST_HOUR]: 'Last hour',
  [DATE_RANGE_PRESETS.LAST_7_DAYS]: 'Last 7 days',
  [DATE_RANGE_PRESETS.LAST_30_DAYS]: 'Last 30 days',
  [DATE_RANGE_PRESETS.LAST_90_DAYS]: 'Last 90 days',
  [DATE_RANGE_PRESETS.LAST_180_DAYS]: 'Last 180 days'
}

/**
 * Gets the start and end timestamps for a date range preset
 * @param {string} preset - Date range preset key
 * @param {Date} referenceDate - Reference date (defaults to now)
 * @returns {Object} Object with from_timestamp and to_timestamp (Unix timestamps in seconds)
 */
export function getDateRangeTimestamps(preset, referenceDate = new Date()) {
  const now = new Date(referenceDate)
  let fromDate, toDate

  switch (preset) {
    case DATE_RANGE_PRESETS.TODAY:
      fromDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0)
      toDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999)
      break

    case DATE_RANGE_PRESETS.THIS_WEEK:
      // Start of week (Sunday)
      const dayOfWeek = now.getDay()
      fromDate = new Date(now)
      fromDate.setDate(now.getDate() - dayOfWeek)
      fromDate.setHours(0, 0, 0, 0)
      
      // End of week (Saturday)
      toDate = new Date(fromDate)
      toDate.setDate(fromDate.getDate() + 6)
      toDate.setHours(23, 59, 59, 999)
      break

    case DATE_RANGE_PRESETS.NEXT_7_DAYS:
      fromDate = new Date(now)
      fromDate.setHours(0, 0, 0, 0)

      toDate = new Date(now)
      toDate.setDate(now.getDate() + 6)
      toDate.setHours(23, 59, 59, 999)
      break

    case DATE_RANGE_PRESETS.THIS_MONTH:
      fromDate = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0)
      toDate = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999)
      break

    case DATE_RANGE_PRESETS.NEXT_30_DAYS:
      fromDate = new Date(now)
      fromDate.setHours(0, 0, 0, 0)

      toDate = new Date(now)
      toDate.setDate(now.getDate() + 29)
      toDate.setHours(23, 59, 59, 999)
      break

    case DATE_RANGE_PRESETS.LAST_HOUR:
      toDate = new Date(now)
      fromDate = new Date(now.getTime() - 60 * 60 * 1000) // 1 hour ago
      break

    case DATE_RANGE_PRESETS.LAST_7_DAYS:
      // 7 whole calendar days ending with today (mirrors NEXT_7_DAYS)
      fromDate = new Date(now)
      fromDate.setDate(now.getDate() - 6)
      fromDate.setHours(0, 0, 0, 0)

      toDate = new Date(now)
      toDate.setHours(23, 59, 59, 999)
      break

    case DATE_RANGE_PRESETS.LAST_30_DAYS:
      fromDate = new Date(now)
      fromDate.setDate(now.getDate() - 29)
      fromDate.setHours(0, 0, 0, 0)

      toDate = new Date(now)
      toDate.setHours(23, 59, 59, 999)
      break

    case DATE_RANGE_PRESETS.LAST_90_DAYS:
      fromDate = new Date(now)
      fromDate.setDate(now.getDate() - 89)
      fromDate.setHours(0, 0, 0, 0)

      toDate = new Date(now)
      toDate.setHours(23, 59, 59, 999)
      break

    case DATE_RANGE_PRESETS.LAST_180_DAYS:
      fromDate = new Date(now)
      fromDate.setDate(now.getDate() - 179)
      fromDate.setHours(0, 0, 0, 0)

      toDate = new Date(now)
      toDate.setHours(23, 59, 59, 999)
      break

    default:
      // Default to next 7 days
      fromDate = new Date(now)
      fromDate.setHours(0, 0, 0, 0)

      toDate = new Date(now)
      toDate.setDate(now.getDate() + 6)
      toDate.setHours(23, 59, 59, 999)
  }

  return {
    from_timestamp: Math.floor(fromDate.getTime() / 1000),
    to_timestamp: Math.floor(toDate.getTime() / 1000)
  }
}

/**
 * Gets the previous period timestamps for comparison
 * For example, if current period is "last 7 days", returns the 7 days before that
 * @param {string} preset - Date range preset key
 * @param {Date} referenceDate - Reference date (defaults to now)
 * @returns {Object} Object with from_timestamp and to_timestamp for the previous period
 */
export function getPreviousPeriodTimestamps(preset, referenceDate = new Date()) {
  // Special handling for "this month" - compare to full previous month
  if (preset === DATE_RANGE_PRESETS.THIS_MONTH) {
    const now = new Date(referenceDate)
    // Get the first day of the previous month
    const previousMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0, 0)
    // Get the last day of the previous month
    const lastDayOfPreviousMonth = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999)
    
    return {
      from_timestamp: Math.floor(previousMonth.getTime() / 1000),
      to_timestamp: Math.floor(lastDayOfPreviousMonth.getTime() / 1000)
    }
  }
  
  const currentPeriod = getDateRangeTimestamps(preset, referenceDate)
  const periodDuration = currentPeriod.to_timestamp - currentPeriod.from_timestamp
  
  // Calculate previous period: shift back by the duration
  return {
    from_timestamp: currentPeriod.from_timestamp - periodDuration,
    to_timestamp: currentPeriod.from_timestamp - 1 // End just before current period starts
  }
}

/**
 * Gets available date range options for select dropdowns
 * @param {boolean} pastOnly - If true, only return past date ranges
 * @param {boolean} futureOnly - If true, only return future date ranges
 * @returns {Array} Array of {value, label} objects
 */
export function getDateRangeOptions(pastOnly = false, futureOnly = false) {
  if (pastOnly) {
    return [
      { value: DATE_RANGE_PRESETS.LAST_HOUR, label: DATE_RANGE_LABELS[DATE_RANGE_PRESETS.LAST_HOUR] },
      { value: DATE_RANGE_PRESETS.TODAY, label: DATE_RANGE_LABELS[DATE_RANGE_PRESETS.TODAY] },
      { value: DATE_RANGE_PRESETS.LAST_7_DAYS, label: DATE_RANGE_LABELS[DATE_RANGE_PRESETS.LAST_7_DAYS] },
      { value: DATE_RANGE_PRESETS.LAST_30_DAYS, label: DATE_RANGE_LABELS[DATE_RANGE_PRESETS.LAST_30_DAYS] },
      { value: DATE_RANGE_PRESETS.LAST_90_DAYS, label: DATE_RANGE_LABELS[DATE_RANGE_PRESETS.LAST_90_DAYS] },
      { value: DATE_RANGE_PRESETS.LAST_180_DAYS, label: DATE_RANGE_LABELS[DATE_RANGE_PRESETS.LAST_180_DAYS] },
      { value: DATE_RANGE_PRESETS.THIS_MONTH, label: DATE_RANGE_LABELS[DATE_RANGE_PRESETS.THIS_MONTH] }
    ]
  }
  
  if (futureOnly) {
    return [
      { value: DATE_RANGE_PRESETS.TODAY, label: DATE_RANGE_LABELS[DATE_RANGE_PRESETS.TODAY] },
      { value: DATE_RANGE_PRESETS.NEXT_7_DAYS, label: DATE_RANGE_LABELS[DATE_RANGE_PRESETS.NEXT_7_DAYS] },
      { value: DATE_RANGE_PRESETS.NEXT_30_DAYS, label: DATE_RANGE_LABELS[DATE_RANGE_PRESETS.NEXT_30_DAYS] },
      { value: DATE_RANGE_PRESETS.THIS_WEEK, label: DATE_RANGE_LABELS[DATE_RANGE_PRESETS.THIS_WEEK] },
      { value: DATE_RANGE_PRESETS.THIS_MONTH, label: DATE_RANGE_LABELS[DATE_RANGE_PRESETS.THIS_MONTH] }
    ]
  }
  
  return Object.entries(DATE_RANGE_LABELS).map(([value, label]) => ({
    value,
    label
  }))
}

/**
 * Formats a date as a YYYY-MM-DD string in local time.
 * @param {Date} date - Date to format
 * @returns {string} Date as 'YYYY-MM-DD'
 */
export function formatDateYMD(date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * Gets the date range as YYYY-MM-DD strings for use in URL parameters.
 * @param {string} preset - Date range preset key
 * @param {Date} referenceDate - Reference date (defaults to now)
 * @returns {Object} Object with from and to as 'YYYY-MM-DD' strings
 */
export function getDateRangeDates(preset, referenceDate = new Date()) {
  const { from_timestamp, to_timestamp } = getDateRangeTimestamps(preset, referenceDate)
  const fromDate = new Date(from_timestamp * 1000)
  const toDate = new Date(to_timestamp * 1000)

  return { from: formatDateYMD(fromDate), to: formatDateYMD(toDate) }
}
